import { useState, useEffect, useRef} from 'react';
import ModalError from '/src/components/Modal/ModalError';
import {InputValidationError} from '/src/utils/error_handling/InputValidationError';
import TextareaAutoResize from '/src/components/TextareaAutoResize';
import InputFile from './InputFile';
import InputLotId from './InputLotId';
import InputPartialDate from './InputPartialDate';
import InputDropdown from './InputDropdown';
// import InputFile from '../InputFile/InputFile';

// Requires config in this format
/* 
    {
        mediaPreview: 'thumbObjUrl',
        saveText: "Mint Item",
        fields: [
            {
                key: 'name',
                title: 'Name',
                tooltip: 'The name of the item',
                type: 'text',
                placeholder: 'Item Name',
                required: true,
            },
            ...
        ]
    }
*/

export default function DynamicEditForm({formConfig, selectedAsset, onSave, onDiscard, onDirty, onChange, className}){
    const [formInput, setFormInput] = useState({});
    const [errors, setErrors] = useState({});
    const [hasRequiredFields, setHasRequiredFields] = useState(false);
    const [configFields, setConfigFields] = useState([]);
    const [isDirty, setIsDirty] = useState(false);
    const inputEls = useRef({});

    // prepare form config
    useEffect(()=>{
        // set flag to show/hide required text tip
        const hasRequired = formConfig && formConfig.fields && formConfig.fields.find(f=>f.required);
        setHasRequiredFields(!!hasRequired);
        // alias config fields for easy access
        setConfigFields(formConfig.fields || []);
    },[formConfig]);

    // pre-populate form with input data
    useEffect(()=>{
        resetForm(formConfig, selectedAsset);
    },[selectedAsset, formConfig]);

    function resetForm(formConfig, data){
        const newData = {};
        const keys = formConfig.fields.map(f=>f.key);
        for(let key of keys){
            let newVal = data && data[key];
            if(newVal || newVal === 0){
                newData[key] = newVal;
            }else{
                newData[key] = "";
            }
        }
        setFormInput(newData);
        setErrors({});
        setIsDirty(false);
    }

    function isFieldBlank(type, value){
        let blank = false;
        switch(type){
            case 'text':
            case 'textarea':
                blank = (/^\s*$/).test(value || '');
                break;
            case 'number':
                blank = isNaN(parseFloat(value)); 
                break;
            default:
                blank = (!value && value !== 0) || !((value + '').length);
        }
        return blank;
    }
    function isFieldOutOfBounds(type, value, min, max){
        if(min === undefined) min = -Infinity;
        if(max === undefined) max = Infinity;

        switch(type){
            case 'text':
            case 'textarea': {
                if(!value) return false;
                const val = (value + '').length;
                if(val < min) return -1;
                if(val > max) return 1;
                break;
            }
            case 'number': {
                const val = parseFloat(value);
                if((!val && val !== 0) || isNaN(val)){
                    return false;
                }
                if(val < min) return -1;
                if(val > max) return 1;
                break;
            }
        }
        return false;
    }

    function onLocalSave(asset, formInput, formConfig, e){
        if(e) e.preventDefault();

        // const fieldNames = formConfig.fields.map(f=>f.key);
        const confFields = formConfig.fields.reduce((o,f)=>{o[f.key] = f; return o;}, {})

        //clear existing errors (based of field names)
		setErrors(errors=>{
            let newErrors = {...errors};
            for(let f of formConfig.fields){
                newErrors[f.key] = null;
            }
            return newErrors;
        });

        // validate form input
        try {
            let formBlank = true;
            for(let key of Object.keys(confFields)){
                const config = confFields[key];
                const value = formInput[key];
                const isBlank = isFieldBlank(config.type, value);
                if(config.required && isBlank){
                    throw new InputValidationError(key, 'required');
                }
                const outOfBounds = isFieldOutOfBounds(config.type, value, config.min, config.max);
                if(outOfBounds){
                    throw new InputValidationError(key, (outOfBounds < 0)?'tooSmall':'tooLarge');
                }
                const outOfBoundsLen = isFieldOutOfBounds(config.type, value, config.min, config.max);
                if(outOfBoundsLen){
                    throw new InputValidationError(key, (outOfBoundsLen < 0)?'tooShort':'tooLong');
                }
                if(!isBlank){
                    formBlank = false;
                }
            }
            if(formConfig.requiredNotBlank && formBlank){
                throw new InputValidationError('_form', 'required');
            }
            // copy asset to new object with field values 
            let newAsset = {...asset};
            for(let f of formConfig.fields){
                newAsset[f.key] = formInput[f.key];
            }
            setIsDirty(false);
            return onSave(newAsset);

        } catch (error) {
            // set error in view (based on field names)
            if(error instanceof InputValidationError){
                let {fieldName, errorName} = error;
                setErrors(errors=>{
                    let newErrors = {...errors};
                    newErrors[fieldName] = errorName;
                    return newErrors;
                });
            }else{
                console.error(error);
                setErrors(errors=>({...errors, unknown: true}));
            }
        }
    }

    function onLocalDiscard(asset, e){
        if(e) e.preventDefault();
        if(onDiscard){
            onDiscard(asset);
        }else{
            resetForm(formConfig, selectedAsset);
        }
    }

    // work-in-progress
    // const onAutoSave = useCallback(()=>{
    //     const configInt = parseInt(autoSave);
    //     const ms = isNaN(configInt)? 1000 : configInt;
    //     clearTimeout(autoSaveTimeout.current);
    //     autoSaveTimeout.current = setTimeout(()=>{
    //         if(isMounted()) onLocalSave(selectedAsset, formInput, formConfig);
    //     }, ms);
    // }, [formInput, selectedAsset, formConfig]);

    // notify parent of dirty change
    useEffect(()=>{
        if(onDirty) onDirty(!!isDirty);
    },[isDirty, onDirty]);

    function handleInput(fieldName, value){
		setFormInput(formInput=>({...formInput, [fieldName]: value}));
		setErrors(errors=>({...errors, [fieldName]: null}));
        setIsDirty(true);
        if(onChange) onChange(fieldName, value);
    }
    function onInputEvent(fieldName, e) {
        let value;
        const type = e.target.type;
        switch(type){
            case 'file':
                value = e.target.files[0];
                break;
            case 'checkbox':
                value = !!e.target.checked;
                break;
            default:
                value = e.target.value;
        }
		handleInput(fieldName, value);
	}

    // autoFocus first blank input (or first if none blank) (enable with formConfig.autoFocus)
    useEffect(()=>{
        if(formConfig && formConfig.autoFocus && selectedAsset && inputEls && !isDirty){
            const confFields = formConfig.fields.reduce((o,f)=>{o[f.key] = f; return o;}, {})

            for(let key of Object.keys(inputEls.current)){
                if(inputEls.current.hasOwnProperty(key)){
                    const type = confFields[key].type;
                    const value = formInput[key];
                    if(isFieldBlank(type, value)){
                        inputEls.current[key].focus();
                        return;
                    }
                }
            }
            const firstKey = formConfig.fields[0].key;
            if(firstKey && inputEls.current.hasOwnProperty(firstKey)){
                const el = inputEls.current[firstKey];
                el.focus();
                el.select();
            }
        }
    }, [formConfig, selectedAsset, inputEls, formInput, isDirty]);

    // automatically focus first input element that has an error
    useEffect(()=>{
        if(errors && inputEls){
            for(let key of Object.keys(inputEls.current)){
                if(inputEls.current.hasOwnProperty(key) && errors[key]){
                    inputEls.current[key].focus();
                    break;
                }
            }
        }
    }, [errors, inputEls]);

    return (
        <form className={`text-left flex ${formConfig.inline? 'flex-row':'flex-col'} ${selectedAsset?'':'text-gray-300'} ${className || ''}`}>  
                {errors.unknown && <ModalError title="Unknown Error" message="An unknown error has occurred" description="Please contact support" callback={()=>{setErrors(errors=>({...errors, unknown: null}))}} />}

                {hasRequiredFields && !formConfig.hideRequiredHint && <p className={`text-gray-700 text-sm mb-1 ${selectedAsset?'':'text-transparent bg-gray-200'}`}><span className={`text-red-600 ${selectedAsset?'':'opacity-0'}`}>*</span> Required fields</p>}
                {errors['_form'] == 'required' && <span className="text-red-600">At least one field must be provided</span>}

                {configFields.map(field=>{
                    const key = field.key;
                    return (<div key={key} className="flex flex-col mt-2">
                        <label htmlFor={"input" + key} className="font-bold">{field.title} {field.required && <span className={`text-red-600 ${selectedAsset?'':'opacity-0'}`}>*</span>}</label>
                        {errors[key] && 
                               (errors[key] == 'notANumber' && <span className="text-red-600">{field.title} must be a number</span>)
                            || (errors[key] == 'zero' && <span className="text-red-600">{field.title} must be greater than 0</span>)
                            || (errors[key] == 'tooSmall' && <span className="text-red-600">{field.title} is too small</span>)
                            || (errors[key] == 'tooLarge' && <span className="text-red-600">{field.title} is too large</span>)
                            || (errors[key] == 'tooLong' && <span className="text-red-600">{field.title} is too long</span>)
                            || (errors[key] == 'tooShort' && <span className="text-red-600">{field.title} is too short</span>)
                            || (errors[key] == 'required' && <span className="text-red-600">{field.title} is required</span>)
                        }
                        {(field.type == 'file' && 
                            <InputFile 
                                className={`form-input ${(errors[key]?"form-input-invalid":"")} ${selectedAsset?"":"form-input-placeholder"}`}
                                name={"input" + field.key}
                                disabled={!selectedAsset}
                                onChange={e=>onInputEvent(field.key, e)}
                                value={formInput[key]}
                                accept={field.accept || undefined}
                                inputRef={el=>inputEls.current[key] = el}
                            />
                        ) || (field.type == 'textarea' &&
                            <TextareaAutoResize 
                                name={"input" + field.key}
                                placeholder={selectedAsset? (field.placeholder || '') : undefined}
                                disabled={!selectedAsset}
                                onChange={e=>onInputEvent(field.key, e)}
                                value={formInput[field.key]}
                                className={`form-input max-w-2xl ${(errors[field.key]?"form-input-invalid":"")} ${selectedAsset?"":"form-input-placeholder resize-none"}`}
                                inputRef={el=>inputEls.current[key] = el}
                            />
                        ) || (field.type == 'lotId' &&
                            <InputLotId 
                                name={"input" + field.key}
                                disabled={!selectedAsset}
                                onChange={e=>onInputEvent(field.key, e)}
                                value={formInput[field.key]}
                                className={`form-input max-w-2xl ${(errors[field.key]?"form-input-invalid":"")} ${selectedAsset?"":"form-input-placeholder resize-none"}`}
                                inputRef={el=>inputEls.current[key] = el}
                                required={field.required}
                            />
                        ) || (field.type == 'partialDate' &&
                            <InputPartialDate 
                                name={"input" + field.key}
                                disabled={!selectedAsset}
                                onChange={e=>onInputEvent(field.key, e)}
                                value={formInput[field.key]}
                                className={`form-input max-w-2xl ${(errors[field.key]?"form-input-invalid":"")} ${selectedAsset?"":"form-input-placeholder resize-none"}`}
                                inputRef={el=>inputEls.current[key] = el}
                                required={field.required}
                            />
                        ) || ((field.type == 'select' || field.type == 'dropdown')  &&
                            <InputDropdown 
                                name={"input" + field.key}
                                disabled={!selectedAsset}
                                onChange={e=>onInputEvent(field.key, e)}
                                value={formInput[field.key]}
                                className={`form-input max-w-2xl ${(errors[field.key]?"form-input-invalid":"")} ${selectedAsset?"":"form-input-placeholder resize-none"}`}
                                inputRef={el=>inputEls.current[key] = el}
                                required={field.required}
                                items={field.items}
                            />
                        ) || (
                            <input
                                id={"input" + key}
                                name={"input" + key}
                                placeholder={field.placeholder || ''}
                                className={`form-input ${(errors[key]?"form-input-invalid":"")} ${selectedAsset?"":"form-input-placeholder"}`}
                                disabled={!selectedAsset}
                                onChange={e=>onInputEvent(field.key, e)}
                                value={formInput[field.key]}
                                type={field.type || 'text'}
                                max={((field.type && field.type !== 'text') && field.max) || ((field.max === 0)?0:undefined)}
                                min={((field.type && field.type !== 'text') && field.min) || ((field.min === 0)?0:undefined)}
                                maxLength={((field.type == 'text' || !field.type) && (field.max || field.maxLength)) || undefined}
                                minLength={((field.type == 'text' || !field.type) && (field.min || field.minLength)) || undefined}
                                step={((field.type == 'number') && field.step) || undefined}
                                ref={el=>inputEls.current[key] = el}
                            />
                        )}
                        {field.tooltip && <small className="text-sm font-light p-1">{field.tooltip}</small>}
                    </div>
                )})}

                <div className={`flex justify-center ${formConfig.inline? 'ml-4 items-end':'items-center'}`}>
                    <button
                        onClick={(e)=>{onLocalSave(selectedAsset, formInput, formConfig, e)}}
                        disabled={!selectedAsset || (formConfig.showSaveOnDirty && !isDirty)}
                        className={`btn flex-grow font-bold mt-6 ${formConfig.inline? 'p-2':'p-4'} shadow-lg uppercase text-lg ${(!formConfig.showSaveOnDirty || isDirty)?"opacity-100":"opacity-0"} ${onDiscard?"border-r-white":""}`}
                        type="submit"
                    >{formConfig.saveText || "Save"}</button>
                    {!formConfig.hideDiscard && <button
                        onClick={(e)=>{onLocalDiscard(selectedAsset, e)}}
                        disabled={!selectedAsset || (formConfig.showSaveOnDirty && !isDirty)}
                        className={`btn basis-1/3 font-light mt-6 ${formConfig.inline? 'p-2':'p-4'} shadow-lg uppercase text-lg whitespace-nowrap ${(!formConfig.showSaveOnDirty || isDirty)?"opacity-100":"opacity-0"}`}
                        type="reset"
                    >{formConfig.discardText || "Discard"}</button>}
                </div>
                
        </form>
    )
}